/**
 * Les lectures de « Travail entre les lives », dans l'ordre de l'ancien écran :
 *  - la présence : l'occurrence (`getLiveOccurrence`), dont seuls les comptes rendus à statistiques
 *    disent qui était connecté. Patron B : la réponse reste ici (`connected`, `null` tant qu'elle se
 *    lit). Un échec est l'erreur de la page (l'ancien écran montrait alors « personne ») ;
 *  - pour chaque élève présent, une fois : la proposition du champion (`studyMissionsService.generate`)
 *    et ses activités déjà à faire (`getStudent`, que le réducteur `currentUser` recopie sur les
 *    élèves du compte).
 *
 * Les deux rafales écrivent N requêtes sur une même tranche : elles ne la lisent pas. Le réducteur
 * `studyMissions` range chaque brouillon et chaque échec par élève (l'élève voyage dans les
 * `urlParams`) ; l'échec d'une lecture des activités se lit par `onResult`, élève par élève. La
 * génération au montage n'est pas un geste du masteur : elle n'est pas mesurée (« Réessayer » l'est).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { liveService } from "../../../../../../data/services/liveService"
import { studentsService } from "../../../../../../data/services/studentsService"
import { studyMissionsService } from "../../../../../../data/services/studyMissionsService"
import {
    connectedStudentIds,
    type ApiPrescriptionOccurrence,
    type PrescriptionRosterEntry,
} from "../../../../adapters/tutor/Lives/tutorPrescriptionAdapter"
import { tutorPrescriptionStore } from "../../../../view-models/tutor/Lives/tutorPrescriptionViewModel"
import { useApiFetch } from "../../../hooks/useApiFetch"

type Id = string | number

/** Ce que dit le bandeau quand les activités déjà à faire d'un élève ne se lisent pas. */
export const TODOS_READ_FAILED = "Ses exercices à faire n'ont pas pu se charger. Réessaie dans un instant."

/** Le corps et les `urlParams` d'une génération : l'élève voyage dans les seconds jusqu'au réducteur. */
export function generateParams(occurrenceId: Id, studentId: Id) {
    return { live_occurrence_id: occurrenceId, student_id: studentId }
}

export function useTutorPrescriptionData(occurrenceId: Id | null, roster: PrescriptionRosterEntry[]) {
    const dispatch = useDispatch()

    const presence = useApiFetch<ApiPrescriptionOccurrence>({
        service: liveService.getLiveOccurrence,
        routeParams: { id: occurrenceId },
        requestKey: occurrenceId != null ? String(occurrenceId) : null,
        enabled: occurrenceId != null,
    })
    const presenceRaw = presence.raw
    const presenceAnswered = !presence.loading && presence.error == null && occurrenceId != null
    const connected = useMemo(
        () => (presenceAnswered ? connectedStudentIds(presenceRaw) : null),
        [presenceAnswered, presenceRaw]
    )

    // Les brouillons déjà là (une sortie de live revisitée) ne se redemandent pas.
    const store = useSelector((state: unknown) => tutorPrescriptionStore(state, occurrenceId))
    const draftsRef = useRef(store.drafts)
    draftsRef.current = store.drafts

    const requestedDrafts = useRef(new Set<string>())
    const requestedTodos = useRef(new Set<string>())
    const [todosFailure, setTodosFailure] = useState<string | null>(null)

    const present = useMemo(
        () => (connected == null ? [] : roster.filter((entry) => connected.has(String(entry.studentId)))),
        [connected, roster]
    )

    useEffect(() => {
        if (occurrenceId == null) return
        for (const { studentId } of present) {
            const key = String(studentId)
            if (draftsRef.current[key] != null || requestedDrafts.current.has(key)) continue
            requestedDrafts.current.add(key)
            const params = generateParams(occurrenceId, studentId)
            dispatch(apiRequest(studyMissionsService.generate, {}, params, params) as never)
        }
    }, [dispatch, occurrenceId, present])

    useEffect(() => {
        for (const { studentId } of present) {
            const key = String(studentId)
            if (requestedTodos.current.has(key)) continue
            requestedTodos.current.add(key)
            dispatch(
                apiRequest(studentsService.getStudent, { id: studentId }, {}, {}, null, null, {
                    offlineDialog: true,
                    onResult: (result: { ok: boolean }) => {
                        if (!result.ok) setTodosFailure(TODOS_READ_FAILED)
                    },
                }) as never
            )
        }
    }, [dispatch, present])

    // Les deux tranches des rafales, rendues au repos au départ : un reste se rejouerait ailleurs.
    useEffect(
        () => () => {
            dispatch(apiIdle(studyMissionsService.generate) as never)
            dispatch(apiIdle(studentsService.getStudent) as never)
        },
        [dispatch]
    )

    return {
        connected,
        error: presence.error,
        refetch: presence.fetch,
        todosFailure,
        dismissTodosFailure: useCallback(() => setTodosFailure(null), []),
    }
}
